import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { CampaignPage } from '@fmnts/api/next';
import {
  DECLARATIVE_FORMS_FIELD_BUILDER_TOKEN,
  DeclarativeFormComponent as FmntsDeclarativeFormComponent,
  ValidationStrategy,
  isFormInputField,
} from '@fmnts/declarative-forms';
import { LegacyStore } from '@formunauts/next/data-access';
import { Observable, ReplaySubject, debounceTime, map, takeUntil } from 'rxjs';
import {
  NextFieldBuilder,
  transformDefaultValue,
  transformFormValuesForStore,
  transformSessionValuesForStore,
} from '../../next-field-builder';

@Component({
  selector: 'next-form',
  templateUrl: './next-form.component.html',
  providers: [
    NextFieldBuilder,
    {
      provide: DECLARATIVE_FORMS_FIELD_BUILDER_TOKEN,
      useExisting: NextFieldBuilder,
    },
  ],
})
export class NextFormComponent implements OnInit, OnDestroy {
  @Input() page!: CampaignPage;
  @Input() pages!: CampaignPage[];
  @Input() get storeId() {
    return this.nextFieldBuilder.storeId;
  }
  set storeId(value: string) {
    this.nextFieldBuilder.storeId = value;
  }

  @Input() validationStrategy = ValidationStrategy.OnSubmit;

  /**
   * Emits when the validation result changes.
   * Only emits when `ValidationStrategy` is set to `OnChange`.
   */
  @Output() isValidChanged = new EventEmitter<boolean>(true);

  @ViewChild('declarativeForm', { static: true })
  private _declarativeForm!: FmntsDeclarativeFormComponent;

  @HostBinding('id') get key(): string {
    return `form-key--${this.storeId}`;
  }

  private destroyed$ = new ReplaySubject<boolean>(1);

  public get declaration() {
    return this._declarativeForm.declaration;
  }

  public get form() {
    return this._declarativeForm.form;
  }

  constructor(
    private store: LegacyStore,
    @Self() private nextFieldBuilder: NextFieldBuilder,
  ) {}

  ngOnInit() {
    // Persist form value on change
    this.form.valueChanges
      .pipe(
        debounceTime(500),
        // Ensure that the most up-to-date value is used, bc sometimes
        // emitted value is outdated, see FO-2351
        map(() => ({
          formDraft: transformFormValuesForStore(this.form),
          sessionValues: transformSessionValuesForStore(
            this.declaration,
            this.form,
          ),
        })),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ formDraft, sessionValues }) => {
        // Store the form values in the store to make them survive reloads
        this.store.set(this.nextFieldBuilder.formValuesStoreId, formDraft);
        // Store session values in a form-independent session key, they won't
        // get updated if the value is null or undefined
        this.store.patch(
          this.nextFieldBuilder.sessionValuesStoreId,
          sessionValues,
        );
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  /**
   * Resets the values for this form
   */
  public resetForm() {
    const defaultValues = this.declaration.allFieldsets.reduce(
      (values, fieldset) => ({
        ...values,
        [fieldset.key]: fieldset.fields
          .filter(isFormInputField)
          .map((field) => ({
            [field.key]: transformDefaultValue(
              field.widgetOptions.defaultValue,
              { field },
            ),
          }))
          .reduce((acc, val) => ({ ...acc, ...val }), {}),
      }),
      {},
    );

    this._declarativeForm.resetForm(defaultValues);
  }

  /**
   * cleares the values stored for this form
   */
  public clearStore() {
    this.store.set(this.nextFieldBuilder.formValuesStoreId, {});
  }

  /**
   * Validates the current form page and awaits async validations
   * Performs the following for each control on the current form page:
   * * Mark as touched
   * * update value and validity
   *
   * @returns
   * An `Observable` that - once validation finished - emits
   * with `true` if the form is valid, then completes.
   */
  public validateFormPage$(): Observable<boolean> {
    return this._declarativeForm.validateFormPage$();
  }

  /**
   * Validates the form and awaits async validations
   *
   * @returns
   * An `Observable` that - once validation finished - emits
   * with `true` if the form is valid, then completes.
   */
  public validate$() {
    return this._declarativeForm.validate$();
  }

  /**
   * @returns
   * An observable that emits with the flattened form value if the form is valid.
   * If the form is invalid, an error is thrown.
   */
  public submit$(): Observable<Record<string, unknown>> {
    return this._declarativeForm.submit$();
  }

  /**
   * @return form value (returned without validation)
   */
  public getFormValue() {
    return this._declarativeForm.getFormValue();
  }
}
